import { Navigate, useNavigate } from 'react-router-dom'
import { Button, EmptyState, Media } from '../../../components/ui'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useTenant } from '../../../tenant/useTenant'
import { setBranch } from '../../../features/booking/selectionSlice'
import { cn } from '../../../utils/cn'
import type { Branch } from '../../../types/storefront'

/** G2 — step 1. A vendor with a single branch never sees this screen: the
 *  wizard settles the branch before routing and this step just steps aside. */
export function LocationStep() {
  const { bootstrap } = useTenant()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const selectedId = useAppSelector((s) => s.selection.branchId)

  if (bootstrap.default_branch_id !== null) return <Navigate to="../services" replace />

  const choose = (branch: Branch) => {
    dispatch(setBranch(branch.id))
    navigate('../services')
  }

  return (
    <div className="container-page py-12 md:py-section-gap-md">
      {/* Stitch centres this step: the cards are the only thing on the page, so
          the heading sits over them rather than off to one side. */}
      <header className="mx-auto mb-16 max-w-2xl text-center">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          Choose a location
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Which salon would you like to visit? Services and available times follow the location you pick.
        </p>
      </header>

      {bootstrap.branches.length === 0 ? (
        <EmptyState
          className="mx-auto max-w-4xl"
          icon="storefront"
          title="No locations are taking bookings"
          body="None of our locations are open for online booking right now. Give us a call and we'll book you in."
        />
      ) : (
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-gutter md:grid-cols-2">
          {bootstrap.branches.map((branch) => {
            const active = selectedId === branch.id
            return (
              <div
                key={branch.id}
                className={cn(
                  'group flex flex-col overflow-hidden rounded-xl border bg-surface-container-lowest transition-colors duration-300',
                  active ? 'border-primary' : 'border-outline-variant hover:border-primary',
                )}
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-surface-container-high">
                  <Media
                    src={branch.image}
                    alt={branch.name}
                    kind="branch"
                    className="transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  {/* The design lifts every image off its container with a
                      hairline inner stroke rather than a shadow. */}
                  <div aria-hidden="true" className="pointer-events-none absolute inset-0 border border-on-secondary/10" />
                </div>

                <div className="flex flex-1 flex-col bg-gradient-to-b from-transparent to-surface-container-low/50 p-8">
                  <div className="mb-6">
                    {branch.city && (
                      <span className="mb-2 block font-label-caps text-label-caps uppercase text-primary">
                        {branch.city}
                      </span>
                    )}
                    <h2 className="mb-2 font-display text-headline-sm text-on-surface">{branch.name}</h2>
                    <p className="flex items-start gap-2 font-body-md text-body-md text-on-surface-variant">
                      <span className="material-symbols-outlined mt-1 text-[18px] text-outline">location_on</span>
                      <span>{branch.address ?? 'Address coming soon'}</span>
                    </p>
                    {branch.phone && (
                      <p className="mt-2 flex items-center gap-2 font-body-md text-body-md text-on-surface-variant">
                        <span className="material-symbols-outlined text-[18px] text-outline">call</span>
                        {branch.phone}
                      </p>
                    )}
                  </div>

                  <div className="mt-auto pt-6">
                    <Button
                      size="lg"
                      variant={active ? 'filled' : 'outlined'}
                      fullWidth
                      onClick={() => choose(branch)}
                    >
                      {active ? 'Continue' : 'Select location'}
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
